import { Error } from '@mui/icons-material';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';

import img from 'components/@auth/images/img.jpg';
import React, { useState } from 'react';
import * as Sentry from '@sentry/react';
import { I18n } from 'utils/i18n';
import { apiUrl } from 'services/apiBaseUrl';
import getSubdomain from 'utils/getSubdomain';

const classes = {
  root: ({ onBoardingImage }) => ({
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundImage: `url(${onBoardingImage})`,
    backgroundSize: 'cover',
  }),
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    alignItems: 'center',
  },
  title: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    gap: 2,
    flexGrow: 1,
    color: 'error.main',
  },
};

// Backend validation pattern: /[A-Za-z0-9-]{2,20}/
const isValidSubdomain = subdomain => {
  if (!subdomain || typeof subdomain !== 'string') return false;
  return /^[A-Za-z0-9-]{2,20}$/.test(subdomain);
};

const ErrorFallback = () => {
  const subdomain = getSubdomain();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackName, setFeedbackName] = useState('');
  const [feedbackEmail, setFeedbackEmail] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Capture the event ID when this component mounts (right after error boundary catches error)
  // Use useEffect to ensure we capture it after the error has been sent to Sentry
  const [errorEventId, setErrorEventId] = useState(null);

  React.useEffect(() => {
    // Give Sentry a moment to capture the error, then get the event ID
    const timeoutId = setTimeout(() => {
      const storedId =
        typeof window !== 'undefined' ? window.__SENTRY_LAST_ERROR_EVENT_ID__ : undefined;
      const lastId = Sentry.lastEventId();
      const capturedId = lastId || storedId;
      if (capturedId) {
        setErrorEventId(capturedId);
      }
    }, 100); // Small delay to ensure error has been sent

    return () => clearTimeout(timeoutId);
  }, []);

  const onBoardingImage = isValidSubdomain(subdomain)
    ? apiUrl(`/v1/company/${subdomain}/logo`)
    : img;

  const handleSubmitFeedback = async e => {
    e.preventDefault();
    if (!feedbackMessage.trim()) return;

    setIsSubmitting(true);
    try {
      // Send feedback to Sentry with the associated event ID
      // The associatedEventId links the feedback to the error event
      Sentry.captureFeedback({
        name: feedbackName.trim() || undefined,
        email: feedbackEmail.trim() || undefined,
        message: feedbackMessage.trim(),
        associatedEventId: errorEventId || undefined,
      });

      setSubmitted(true);
      setTimeout(() => {
        setFeedbackOpen(false);
        setSubmitted(false);
        setFeedbackName('');
        setFeedbackEmail('');
        setFeedbackMessage('');
      }, 2000);
    } catch (err) {
      console.error('Failed to send feedback:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseFeedback = () => {
    if (!isSubmitting) {
      setFeedbackOpen(false);
      setFeedbackName('');
      setFeedbackEmail('');
      setFeedbackMessage('');
      setSubmitted(false);
    }
  };

  return (
    <>
      <Box sx={classes.root({ onBoardingImage })}>
        <Card>
          <CardContent sx={classes.card}>
            <Box sx={classes.title}>
              <Error fontSize="large" />
              <Typography variant="h5">{I18n.t('ErrorFallback.Title')}</Typography>
            </Box>
            <Typography>{I18n.t('ErrorFallback.Description')}</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button variant="outlined" onClick={() => setFeedbackOpen(true)}>
                {I18n.t('ErrorFallback.Report issue') || 'Report what happened'}
              </Button>
              <Button variant="contained" component={Link} href="/">
                {I18n.t('ErrorFallback.Go to the home page')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Dialog open={feedbackOpen} onClose={handleCloseFeedback} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmitFeedback}>
          <DialogTitle>
            {I18n.t('ErrorFallback.Feedback.Title') || 'Tell us what happened'}
          </DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <TextField
                value={feedbackName}
                onChange={e => setFeedbackName(e.target.value)}
                fullWidth
                size="small"
                label={I18n.t('ErrorFallback.Feedback.Name') || 'Your name (optional)'}
              />
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <TextField
                type="email"
                value={feedbackEmail}
                onChange={e => setFeedbackEmail(e.target.value)}
                fullWidth
                size="small"
                label={I18n.t('ErrorFallback.Feedback.Email') || 'Your email (optional)'}
              />
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <TextField
                value={feedbackMessage}
                onChange={e => setFeedbackMessage(e.target.value)}
                fullWidth
                multiline
                rows={4}
                required
                label={I18n.t('ErrorFallback.Feedback.Message') || 'What happened?'}
                autoFocus
              />
            </FormControl>
            {submitted && (
              <Typography
                sx={{
                  color: 'success.main',
                  mt: 1,
                }}
              >
                {I18n.t('ErrorFallback.Feedback.ThankYou') ||
                  'Thank you! Your feedback has been sent.'}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseFeedback} disabled={isSubmitting} variant="outlined">
              {I18n.t('ErrorFallback.Feedback.Cancel') || 'Cancel'}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || !feedbackMessage.trim() || submitted}
            >
              {submitted
                ? I18n.t('ErrorFallback.Feedback.Sent') || 'Sent!'
                : I18n.t('ErrorFallback.Feedback.Send') || 'Send Feedback'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default ErrorFallback;
